import { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Grid, MenuItem, Stack, TextField, Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import BuildCircleRoundedIcon from "@mui/icons-material/BuildCircleRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { AppLoader, AppSearchField, AppSnackbar, AppToolbar } from "../../../../components/common";
import NotificationCard from "../components/NotificationCard";
import PreventiveMaintenanceTable from "../components/PreventiveMaintenanceTable";
import usePreventiveMaintenance from "../hooks/usePreventiveMaintenance";
import preventiveMaintenanceService from "../api/preventiveMaintenance.service";
import maintenanceService from "../../maintenance/api/maintenance.service";
import { PreventiveMaintenance } from "../types";
import { HubOption, Technician } from "../../maintenance/types";
import { SparePageHeader, SpareSummary, sparePageSx } from "../../components/SparePagePresentation";

interface PreventiveMaintenancePageProps { embedded?: boolean; }

const PreventiveMaintenancePage = ({ embedded = false }: PreventiveMaintenancePageProps) => {
  const {
    preventiveMaintenances, analytics, notifications, pagination, filters, loading, error, syncWarning,
    refresh, createInHouseRepair, markNotificationAsRead, updateFilters,
  } = usePreventiveMaintenance();
  const [selectedMaintenance, setSelectedMaintenance] = useState<PreventiveMaintenance | null>(null);
  const [createRepairOpen, setCreateRepairOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [repairAmeId, setRepairAmeId] = useState("");
  const [repairRemarks, setRepairRemarks] = useState("");
  const [hubs, setHubs] = useState<HubOption[]>([]);
  const [ames, setAmes] = useState<Technician[]>([]);
  const [amesLoading, setAmesLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" as "success" | "error" });
  const navigate = useNavigate();
  const hubNames = useMemo(() => Object.fromEntries(hubs.map((hub) => [hub.id, hub.name])), [hubs]);
  const unreadNotifications = notifications.filter((notification) => !notification.isRead);

  useEffect(() => {
    maintenanceService.getHubs().then(setHubs).catch(() => setHubs([]));
  }, []);

  useEffect(() => {
    if (!createRepairOpen || !selectedMaintenance) return;
    const hub = hubs.find((item) => item.id === selectedMaintenance.warehouseId);
    if (!hub?.cityId) { setAmes([]); return; }
    let cancelled = false;
    setAmesLoading(true);
    maintenanceService.getAmes({ cityId: hub.cityId })
      .then((items) => { if (!cancelled) setAmes(items); })
      .catch(() => { if (!cancelled) setAmes([]); })
      .finally(() => { if (!cancelled) setAmesLoading(false); });
    return () => { cancelled = true; };
  }, [createRepairOpen, selectedMaintenance, hubs]);

  const cards = [
    { label: "Deployed Vehicles", value: analytics?.totalScheduled ?? 0, icon: <BuildCircleRoundedIcon />, color: "#2563eb" },
    { label: "Due Soon", value: analytics?.dueSoon ?? 0, icon: <EventAvailableRoundedIcon />, color: "#d97706" },
    { label: "Due Today", value: analytics?.dueToday ?? 0, icon: <NotificationsActiveRoundedIcon />, color: "#0288d1" },
    { label: "Overdue", value: analytics?.overdue ?? 0, icon: <WarningAmberRoundedIcon />, color: "#d32f2f" },
  ];

  const closeRepair = () => {
    if (submitting) return;
    setCreateRepairOpen(false);
    setSelectedMaintenance(null);
    setRepairAmeId("");
    setRepairRemarks("");
  };

  if (loading && !analytics && !error) return <AppLoader />;

  return (
    <Box sx={embedded ? undefined : sparePageSx("preventive")}>
      {embedded ? (
        <Typography variant="subtitle1" fontWeight={800} mb={2}>Preventive Schedules</Typography>
      ) : (
        <SparePageHeader kind="preventive" title="Preventive Maintenance" subtitle="Deployed vehicle service schedules" />
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {syncWarning && <Alert severity="warning" sx={{ mb: 2 }}>{syncWarning}</Alert>}
      <SpareSummary items={cards} />
      <AppToolbar
        left={<AppSearchField placeholder="Search schedule, vehicle or rider..." value={filters.search ?? ""}
          onChange={(search) => updateFilters({ search, page: 1 })} />}
        right={<Button disabled={loading} startIcon={<RefreshIcon />} onClick={() => refresh(false, true)}>{loading ? "Refreshing..." : "Refresh"}</Button>}
      />
      {unreadNotifications.length > 0 && (
        <Grid container spacing={2} mb={3}>
          {unreadNotifications.slice(0, 3).map((notification) => (
            <Grid item xs={12} md={4} key={notification.id}>
              <NotificationCard notification={notification} onMarkAsRead={markNotificationAsRead}
                onView={async (id) => {
                  try {
                    const schedule = preventiveMaintenances.find((item) => item.id === id) ||
                      await preventiveMaintenanceService.getPreventiveMaintenanceById(id);
                    setSelectedMaintenance(schedule);
                    setDetailsOpen(true);
                  } catch (requestError: any) {
                    setSnackbar({ open: true, message: requestError?.response?.data?.message || "Unable to open preventive schedule.", severity: "error" });
                  }
                }} />
            </Grid>
          ))}
        </Grid>
      )}
      <PreventiveMaintenanceTable data={preventiveMaintenances} hubNames={hubNames}
        page={pagination.page} pageSize={pagination.limit} total={pagination.total}
        onPageChange={(page) => updateFilters({ page })}
        onRowsPerPageChange={(limit) => updateFilters({ page: 1, limit })}
        onCreateInHouseRepair={(schedule) => {
          setSelectedMaintenance(schedule); setRepairAmeId(""); setRepairRemarks(""); setAmes([]); setCreateRepairOpen(true);
        }}
        onViewInHouseTicket={(schedule) => navigate(`/spare-parts/maintenance?serviceType=IN_HOUSE&ticketId=${encodeURIComponent(schedule.generatedTicket?.id || "")}`)}
      />
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{selectedMaintenance?.maintenanceNumber}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {[
              ["Vehicle", selectedMaintenance?.vehicleNumber],
              ["Rider ID", selectedMaintenance?.riderDisplayId],
              ["Hub", hubNames[selectedMaintenance?.warehouseId || ""]],
              ["Deployed", selectedMaintenance?.deploymentDate ? new Date(selectedMaintenance.deploymentDate).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" }) : "-"],
              ["Next Service", selectedMaintenance ? new Date(selectedMaintenance.nextServiceDate).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" }) : "-"],
            ].map(([label, value]) => <Box key={label}><Typography color="text.secondary" variant="body2">{label}</Typography><Typography sx={{ overflowWrap: "anywhere" }}>{value || "-"}</Typography></Box>)}
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setDetailsOpen(false)}>Close</Button></DialogActions>
      </Dialog>
      <Dialog open={createRepairOpen} onClose={closeRepair} fullWidth maxWidth="sm">
        <DialogTitle>Create In-House Repair</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography>Confirm vehicle {selectedMaintenance?.vehicleNumber || "-"} is at {hubNames[selectedMaintenance?.warehouseId || ""] || "its hub"}.</Typography>
            <TextField label="Hub" value={hubNames[selectedMaintenance?.warehouseId || ""] || ""} InputProps={{ readOnly: true }} fullWidth />
            <TextField select required label="Notify AME" value={repairAmeId} disabled={amesLoading || submitting}
              helperText={!amesLoading && !ames.length ? "No AME is configured for this hub's city." : undefined}
              onChange={(event) => setRepairAmeId(event.target.value)} fullWidth>
              {ames.map((ame) => <MenuItem key={ame.id} value={ame.id}>{ame.name}</MenuItem>)}
            </TextField>
            <TextField label="Remarks" value={repairRemarks} onChange={(event) => setRepairRemarks(event.target.value)} multiline minRows={3} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="outlined" disabled={submitting} onClick={closeRepair}>Cancel</Button>
          <Button variant="contained" disabled={!selectedMaintenance?.warehouseId || !repairAmeId || submitting || amesLoading}
            onClick={async () => {
              if (!selectedMaintenance) return;
              setSubmitting(true);
              try {
                const result = await createInHouseRepair(selectedMaintenance.id, {
                  warehouseId: selectedMaintenance.warehouseId || undefined, notifyAmeId: repairAmeId,
                  confirmedVehicleAtHub: true, remarks: repairRemarks || undefined,
                });
                setCreateRepairOpen(false); setSelectedMaintenance(null);
                navigate(`/spare-parts/maintenance?serviceType=IN_HOUSE&ticketId=${encodeURIComponent(result.ticket.id)}`);
              } catch (requestError: any) {
                setSnackbar({ open: true, message: requestError?.response?.data?.message || "Unable to create preventive repair.", severity: "error" });
              } finally { setSubmitting(false); }
            }}> {submitting ? "Creating..." : "Confirm and Create Repair"}</Button>
        </DialogActions>
      </Dialog>
      <AppSnackbar open={snackbar.open} message={snackbar.message} severity={snackbar.severity}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))} />
    </Box>
  );
};

export default PreventiveMaintenancePage;
